/* eslint-disable @typescript-eslint/no-explicit-any -- dynamic PostgREST/Supabase rows are shaped at runtime; typed at the boundary. TODO: replace with generated row types. */
/**
 * useAccountInsights — @flaxitup account-level daily metrics (followers,
 * reach, profile views) + reels leaderboard for the last 30 days.
 * Sourced from marketing.meta_account_daily + marketing.meta_posts.
 */
import { useQuery } from '@tanstack/react-query';
import { marketing } from '@/lib/supabase/marketing';

export interface AccountDaily {
  captured_on: string;
  followers_count: number | null;
  follows_count: number | null;
  media_count: number | null;
  reach: number | null;
  profile_views: number | null;
  website_clicks: number | null;
  accounts_engaged: number | null;
}

export interface ReelRow {
  media_id: string;
  permalink: string | null;
  thumbnail_url: string | null;
  caption: string | null;
  published_at: string;
  views: number | null;
  reach: number | null;
  saved: number | null;
  like_count: number;
  comments_count: number;
  total_interactions: number | null;
  avg_watch_time_s: number | null;   // parsed from raw.reels
}

export function useAccountInsights(days = 30) {
  return useQuery({
    queryKey: ['account-insights', days],
    queryFn: async () => {
      const cutoff = new Date(Date.now() - days * 86400_000).toISOString().slice(0, 10);
      const [dailyRes, reelsRes] = await Promise.all([
        marketing
          .from('meta_account_daily')
          .select('captured_on,followers_count,follows_count,media_count,reach,profile_views,website_clicks,accounts_engaged')
          .gte('captured_on', cutoff)
          .order('captured_on', { ascending: true }),
        marketing
          .from('meta_posts')
          .select('media_id,permalink,thumbnail_url,caption,published_at,views,reach,saved,like_count,comments_count,total_interactions,raw')
          .eq('provider', 'instagram')
          .in('media_type', ['REEL', 'VIDEO'])
          .gte('published_at', new Date(Date.now() - days * 86400_000).toISOString())
          .order('views', { ascending: false, nullsFirst: false })
          .limit(10),
      ]);
      if (dailyRes.error) throw dailyRes.error;
      if (reelsRes.error) throw reelsRes.error;

      const daily = (dailyRes.data ?? []) as AccountDaily[];
      const latest = daily[daily.length - 1] ?? null;
      const first = daily[0] ?? null;
      const followerDelta = latest && first && latest.followers_count != null && first.followers_count != null
        ? latest.followers_count - first.followers_count
        : null;

      const reels: ReelRow[] = (reelsRes.data ?? []).map((r: any) => {
        const raw = (r.raw ?? {}) as any;
        const rr = raw.reels ?? {};
        // ig_reels_avg_watch_time comes back in ms
        const ms = rr.ig_reels_avg_watch_time ?? null;
        return {
          media_id: r.media_id,
          permalink: r.permalink,
          thumbnail_url: r.thumbnail_url,
          caption: r.caption,
          published_at: r.published_at,
          views: r.views,
          reach: r.reach,
          saved: r.saved,
          like_count: r.like_count,
          comments_count: r.comments_count,
          total_interactions: r.total_interactions,
          avg_watch_time_s: ms != null ? Math.round(ms / 100) / 10 : null,
        };
      });

      // Top-performing posts (by total_interactions or likes fallback) in the window
      const topRes = await marketing
        .from('meta_posts')
        .select('media_id,permalink,thumbnail_url,caption,media_type,published_at,like_count,comments_count,views,reach,saved,total_interactions')
        .eq('provider', 'instagram')
        .gte('published_at', new Date(Date.now() - days * 86400_000).toISOString())
        .order('total_interactions', { ascending: false, nullsFirst: false })
        .limit(6);
      const topPosts = (topRes.data ?? []) as TopPostRow[];

      // Engagement rate — sum of interactions / sum of reach across the window
      const totalReach = topPosts.reduce((s, p) => s + (p.reach ?? 0), 0);
      const totalInteractions = topPosts.reduce((s, p) => s + (p.total_interactions ?? p.like_count + p.comments_count), 0);
      const engagementRate = totalReach > 0 ? totalInteractions / totalReach : null;

      return { daily, latest, first, followerDelta, reels, topPosts, engagementRate };
    },
    staleTime: 60_000,
  });
}

export interface TopPostRow {
  media_id: string;
  permalink: string | null;
  thumbnail_url: string | null;
  caption: string | null;
  media_type: string | null;
  published_at: string;
  like_count: number;
  comments_count: number;
  views: number | null;
  reach: number | null;
  saved: number | null;
  total_interactions: number | null;
}

/** Fire meta-account-insights-sync (proxied) — for the "Refresh" button. */
export async function syncAccountInsights(): Promise<{ ok: boolean; error?: string; captured_on?: string; followers?: number }> {
  const r = await fetch('/api/content/account-insights-sync', { method: 'POST', headers: { 'content-type': 'application/json' }, body: '{}' });
  const j = await r.json();
  if (!r.ok || j.error) return { ok: false, error: j.error ?? `HTTP ${r.status}` };
  return { ok: true, captured_on: j.captured_on, followers: j.followers };
}
